import { REBATE_CLAIM_WINDOW_MAX_DAYS } from '@b1fr0st/contracts/domain/rebate-claim-window';

/**
 * Accrual period of a rebate-kind promotion (`cashback` / `turnover_rebate`).
 *
 * The period is not a column — operators write it into `conditions.periodMode`
 * from the backoffice rebate form, and it decides both what the player is paid
 * and how often. This resolver is a deliberate mirror of `periodModeOf` in
 * `apps/promo/src/repo/rebate.ts`, fallback included: copy that names a period
 * the payout does not use is the defect this exists to prevent, and an
 * independently chosen default would reintroduce it for every promotion whose
 * `periodMode` an operator never set.
 */
export const REBATE_PERIOD_MODES = ['daily', 'weekly', 'monthly'] as const;

export type RebatePeriodMode = (typeof REBATE_PERIOD_MODES)[number];

export function rebatePeriodMode(
  kind: string,
  conditions: Record<string, unknown> | null | undefined,
): RebatePeriodMode {
  const mode = conditions?.['periodMode'];
  if (mode === 'daily' || mode === 'weekly' || mode === 'monthly') return mode;
  return kind === 'turnover_rebate' ? 'daily' : 'weekly';
}

/**
 * Weekday (0 = Sunday … 6 = Saturday) and day-of-month a period opens on.
 *
 * Mirror of `parsePeriodAnchor` / `DEFAULT_PERIOD_ANCHOR` in
 * `apps/promo/src/repo/periods.ts`, ranges and fallbacks included. The card
 * names the day the member may claim on, and that day IS the anchor: the
 * weekly window runs anchor-to-anchor and the monthly window opens on the
 * anchor date. An unreadable value falls back rather than throwing, because
 * `conditions` is operator-typed JSON with no schema behind it.
 */
export interface RebatePeriodAnchor {
  dow: number;
  dom: number;
}

export const DEFAULT_REBATE_PERIOD_ANCHOR: RebatePeriodAnchor = { dow: 1, dom: 1 };

function intInRange(raw: unknown, min: number, max: number, fallback: number): number {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : Number.NaN;
  return Number.isInteger(n) && n >= min && n <= max ? n : fallback;
}

export function rebatePeriodAnchor(
  conditions: Record<string, unknown> | null | undefined,
): RebatePeriodAnchor {
  return {
    dow: intInRange(conditions?.['periodAnchorDow'], 0, 6, DEFAULT_REBATE_PERIOD_ANCHOR.dow),
    dom: intInRange(conditions?.['periodAnchorDom'], 1, 28, DEFAULT_REBATE_PERIOD_ANCHOR.dom),
  };
}

/**
 * Days a closed period stays claimable.
 *
 * Mirror of `claimWindowDaysOf` in `apps/promo/src/repo/rebate.ts` together with
 * the next-period cap `claimDeadline` in `apps/promo/src/repo/rebate-window.ts`
 * applies to it. The default is not cosmetic: the server enforces the window
 * whether or not an operator set one, and an accrual nobody claimed inside it is
 * gone. So the card always states the effective number, never stays silent on
 * the ones it inherited — and never repeats a stored number the server cuts, or
 * a daily rebate saved with 30 days would promise a month it pays for one.
 *
 * A blank string is unset, as it is to the server: `Number('')` is 0, and a
 * cleared form field would otherwise read as a "0-day window".
 *
 * Daily and weekly periods are a fixed length, so the capped number is exactly
 * the server's window. A month is not: see `rebateClaimWindowEndsWithNextPeriod`.
 */
export function rebateClaimWindowDays(
  mode: RebatePeriodMode,
  conditions: Record<string, unknown> | null | undefined,
): number {
  const max = REBATE_CLAIM_WINDOW_MAX_DAYS[mode];
  const stored = conditions?.['claimWindowDays'];
  const unset =
    stored === null || stored === undefined || (typeof stored === 'string' && stored.trim() === '');
  const raw = unset ? max : Number(stored);
  const days = Number.isFinite(raw) && raw >= 0 ? raw : max;
  return Math.min(days, max);
}

/** The shortest a monthly period runs: an anchor-to-anchor window that spans February. */
const SHORTEST_MONTHLY_PERIOD_DAYS = 28;

/**
 * Whether `days` may outlast the next period, so the card has to say the window
 * also ends with it.
 *
 * The server ends a monthly window at the real end of the next month, which is
 * 28 to 31 days after the period closed depending on the month. A window longer
 * than 28 days is therefore sometimes shorter than it says, and the card, which
 * has no period in hand, can only be exact by saying both.
 */
export function rebateClaimWindowEndsWithNextPeriod(mode: RebatePeriodMode, days: number): boolean {
  return mode === 'monthly' && days > SHORTEST_MONTHLY_PERIOD_DAYS;
}
